import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';
import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, CompanyDTO, CompanyFilterDTO, ValueListDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField, IChipsSettingProps } from '../../../shared/components';
import { CompanyMaintenance } from './CompanyMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { People } from '../people/People';
import { CompanyPeopleAssociate } from './CompanyPeopleAssociate';
import { useTranslation } from 'react-i18next';

export const Company: React.FC = () => {

    const [mainActivityList, setMainCompanyList] = useState<ValueListDTO[]>([]);
    const [businessRelationshipTypeList, setBusinessRelationshipList] = useState<ValueListDTO[]>([]);
    const [statusList, setStatusList] = useState<ValueListDTO[]>([]);
    const [unityTypeList, setUnityTypoeList] = useState<ValueListDTO[]>([]);
    const [ownerCompanyList, setOnwerCompanyList] = useState<CompanyDTO[]>([]);
    const [companyList, setCompanyList] = useState<CompanyDTO[]>([]);
    const [companyFilterDTO, setCompanyFilterDTO] = useState<CompanyFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);
    const [selectRecord, setSelectRecord] = useState('');

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');
    const { t } = useTranslation();

    useEffect(() => {
        setCompanyFilterDTO({
            ...companyFilterDTO,
            name: '',
            code: '',
            mainActivityId: '',
            businessRelationshipTypeId: '',
            statusId: '',
            unityTypeId: '',
            ownerCompanyId: '',
        } as CompanyFilterDTO);
    }, []);

    useEffect(() => {
        handleCompanyGetAllViewAsync();
        handleOwnerCompanyGetAllViewAsync();
        handleListGetAll(ListId.corpsphere.company.mainActivityId, setMainCompanyList);
        handleListGetAll(ListId.corpsphere.company.businessRelationshipTypeId, setBusinessRelationshipList);
        handleListGetAll(ListId.corpsphere.company.statusId, setStatusList);
        handleListGetAll(ListId.corpsphere.company.unityTypeId, setUnityTypoeList);

        handleGetPermission(ListId.corpsphere.companyId, setAccessRolePermissionDTO);
    }, [companyFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleCompanyGetAllViewAsync();
    }, [actionExecuted]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);

    const handleListGetAll = useCallback(async (listId: string, setList: any) => {
        const valueListFilterDTO = {
            value: '',
            listId: listId,
            orderBy: 'value ASC'
        };
        GenericService.GetAllAsync1(
            'parmsphere.valueList',
            valueListFilterDTO,
            setLoading,
            setList,
            showMessage
        );
    }, []);

    const handleOwnerCompanyGetAllViewAsync = useCallback(async () => {
        const companyLocalFilterDTO = {

        };

        GenericService.GetAllViewAsync1('corpsphere.company', companyLocalFilterDTO, setActionExecuted, setLoading, setOnwerCompanyList, showMessage, true);
    }, []);

    const handleCompanyGetAllViewAsync = useCallback(async () => {
        if (companyFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return;
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage(t('message.allowGetAllView'), 'info');
            return;
        }
        GenericService.GetAllViewAsync1('corpsphere.company', companyFilterDTO, setActionExecuted, setLoading, setCompanyList, showMessage, true);
    }, [companyFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setCompanyFilterDTO({
            ...companyFilterDTO,
            name: '',
            code: '',
            mainActivityId: '',
            businessRelationshipTypeId: '',
            statusId: '',
            unityTypeId: '',
            ownerCompanyId: '',
        } as CompanyFilterDTO);
    };

    function handleSearchClick() {
        handleCompanyGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage(t('message.allowAdd'), 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage(t('message.allowGetById'), 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage(t('message.allowDelete'), 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setSelectRecord('');
        setIsOpenMaintenance(true);
    };

    const handleAssociateUsers = (data: string) => {
        setNewRecord('');
        setDeleteRecord('');
        setEditRecord('');
        setSelectRecord(data);
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: t('company.button.new'), icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: t('company.cardActions.viewDetails'), icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'associateUsers', toolTip: t('company.cardActions.associateUsers'), icon: 'group_outlined', iconColor: 'info', setAction: handleAssociateUsers },
        { id: 'delete', toolTip: t('company.cardActions.delete'), icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: t('company.chipFilters.totalRecords'), value: ownerCompanyList.length },
        { label: t('company.chipFilters.name'), value: companyFilterDTO?.name },
        { label: t('company.columns.code'), value: companyFilterDTO?.code },
        { label: t('company.columns.mainActivity'), value: mainActivityList.find(x => x.id === companyFilterDTO?.mainActivityId)?.value },
        { label: t('company.columns.businessRelationshipType'), value: businessRelationshipTypeList.find(x => x.id === companyFilterDTO?.businessRelationshipTypeId)?.value },
        { label: t('company.columns.status'), value: statusList.find(x => x.id === companyFilterDTO?.statusId)?.value },
        { label: t('company.columns.unityType'), value: unityTypeList.find(x => x.id === companyFilterDTO?.unityTypeId)?.value },
        { label: t('company.columns.ownerCompany'), value: ownerCompanyList.find(x => x.id === companyFilterDTO?.ownerCompanyId)?.name },
    ];

    const chipStatus : IChipsSettingProps[] = [
        { value: t('company.chipStatus.active'), iconName: 'task_alt_outlined', iconColor: 'success' },
        { value: t('company.chipStatus.inactive'), iconName: 'block_outlined', iconColor: 'warning' },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: t('company.columns.name'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'code', label: t('company.columns.code'), size: 2, type: 'text', visible: true, isChip: true, hasFieldLine: false, hasToolTip: true },
        { field: 'mainActivity', label: t('company.columns.mainActivity'), size: 4, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'businessRelationshipType', label: t('company.columns.businessRelationshipType'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'status', label: t('company.columns.status'), size: 4, type: 'text', visible: true, isChip: true, chipSettings: chipStatus, hasFieldLine: false, hasToolTip: true },
        { field: 'unityType', label: t('company.columns.unityType'), size: 2, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }

    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='COMPANY'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='name'
                                label={t('company.filter.name')}
                                placeholder={t('company.filter.name')}
                                setValue={setCompanyFilterDTO}
                                value={companyFilterDTO?.name}
                                obj={companyFilterDTO}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <TextFieldInput
                                id='code'
                                label={t('company.filter.code')}
                                placeholder={t('company.filter.code')}
                                setValue={setCompanyFilterDTO}
                                value={companyFilterDTO?.code}
                                obj={companyFilterDTO}
                                maxLength={200}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='mainActivityId'
                                label={t('company.filter.mainActivity')}
                                placeholder={t('company.filter.mainActivity')}
                                setValue={setCompanyFilterDTO}
                                value={companyFilterDTO?.mainActivityId}
                                obj={companyFilterDTO}
                                valueList={mainActivityList}
                                hasNoneSelect={true}
                                searchable={true}
                                labelKey='value'
                                valueKey='id'
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='businessRelationshipTypeId'
                                label={t('company.filter.businessRelationshipType')}
                                placeholder={t('company.filter.businessRelationshipType')}
                                setValue={setCompanyFilterDTO}
                                value={companyFilterDTO?.businessRelationshipTypeId}
                                obj={companyFilterDTO}
                                valueList={businessRelationshipTypeList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='statusId'
                                label={t('company.filter.status')}
                                placeholder={t('company.filter.status')}
                                setValue={setCompanyFilterDTO}
                                value={companyFilterDTO?.statusId}
                                obj={companyFilterDTO}
                                valueList={statusList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='unityTypeId'
                                label={t('company.filter.unityType')}
                                placeholder={t('company.filter.unityType')}
                                setValue={setCompanyFilterDTO}
                                value={companyFilterDTO?.unityTypeId}
                                obj={companyFilterDTO}
                                valueList={unityTypeList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <SelectField
                                id='ownerCompanyId'
                                label={t('company.filter.ownerCompany')}
                                placeholder={t('company.filter.businessRelationshipType')}
                                setValue={setCompanyFilterDTO}
                                value={companyFilterDTO?.ownerCompanyId}
                                obj={companyFilterDTO}
                                valueList={ownerCompanyList}
                                hasNoneSelect={true}
                            />
                        </Grid>
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <Box sx={{ display: 'flex', maxHeight: '80vh', width: '100%', overflow: 'hidden', overflowY: 'auto' }}>
                    <CardList
                        columns={columns}
                        rows={companyList}
                        actions={cardActions}
                        hasChildren={true}
                        setRowExpanded={setExpandedData}
                        cardSize={12}
                        childrenTitle={'Associate Peoples'}
                        childrenComponent={(
                            <Box width={'100%'}>
                                <People data={expadedData} />
                            </Box>
                        )}
                    />
                </Box>
                {newRecord && (
                    <CompanyMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {editRecord && (
                    <CompanyMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'corpsphere.company'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />
                )}
                {selectRecord && (
                    <CompanyPeopleAssociate
                        data={selectRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
            </LayoutDashboard>
        </React.Fragment >
    );
};
